// eslint-disable-next-line import/no-extraneous-dependencies
import CryptoJS from 'crypto-js';

const isDataLayerExist = () => typeof window !== 'undefined' && window.dataLayer;

type GTMEventData = Record<string, unknown>;

/**
 * Track the registration start event in GTM. The registration may have multiple steps, so we need to track the step name.
 * @param stepName - The step name of the registration form.
 * @param additionalData - Additional data to be added to the GTM data layer event.
 */
export const trackRegistrationEvent = (stepName: string, additionalData?: GTMEventData) => {
  if (isDataLayerExist()) {
    window.dataLayer.push({
      event: 'dle_pharmacist_registration_start',
      step_name: stepName,
      clickId: 'pharmacist_funnel',
      ...additionalData,
    });
  }
};

/**
 * Track the registration complete event in GTM. The registration may have multiple steps, so we need to track the step name.
 * @param stepName - The step name of the registration form.
 * @param additionalData - Additional data to be added to the GTM data layer event.
 */
export const trackRegistrationCompleteEvent = (stepName: string, additionalData?: GTMEventData) => {
  if (isDataLayerExist()) {
    window.dataLayer.push({
      event: 'dle_pharmacist_registration_complete',
      step_name: stepName,
      clickId: 'pharmacist_funnel',
      ...additionalData,
    });
  }
};

/**
 * Track the registration step 1 complete event in GTM.
 * @param pharmacyName - The name of the pharmacy.
 * @param email - The email of the pharmacy.
 */
export const trackRegistrationStep1Complete = ({ pharmacyName, email }: { pharmacyName: string; email: string }) => {
  const hashedEmail = CryptoJS.SHA256(email.toLowerCase()).toString();

  trackRegistrationEvent('registration_1_1_complete', {
    pharmacy_name: pharmacyName,
    pharmacy_email_hashed: hashedEmail,
  });
};

/**
 * Track the registration step 2 complete event in GTM.
 * @param abn - The ABN of the pharmacy.
 */
export const trackRegistrationStep2Complete = ({ abn }: { abn: string }) => {
  const hashedABN = CryptoJS.SHA256(abn).toString();

  trackRegistrationCompleteEvent('registration_1_2_register_now_btn', {
    abn_hashed: hashedABN,
  });
};

/**
 * Track the registration complete event in GTM.
 * @param stepName - The step name of the registration form.
 * @param pharmacyName - The name of the pharmacy.
 * @param email - The email of the pharmacy.
 * @param abn - The ABN of the pharmacy.
 * @param monthlySpend - The monthly spend of the pharmacy.
 * @param compoundingPharmacy - The compounding pharmacy of the pharmacy.
 * @param ahpraNumber - The AHPRA number of the pharmacy.
 */
export const trackRegistrationCompletion = ({
  stepName,
  pharmacyName,
  email,
  abn,
  monthlySpend,
  compoundingPharmacy,
  ahpraNumber,
}: {
  stepName: string;
  pharmacyName: string;
  email: string;
  abn: string;
  monthlySpend?: string;
  compoundingPharmacy?: string;
  ahpraNumber?: string;
}) => {
  const hashedEmail = CryptoJS.SHA256(email.toLowerCase()).toString();
  const hashedABN = CryptoJS.SHA256(abn).toString();
  const hashedAHPRA = CryptoJS.SHA256(ahpraNumber ?? '').toString();

  trackRegistrationCompleteEvent(stepName, {
    pharmacy_name: pharmacyName,
    pharmacy_email_hashed: hashedEmail,
    abn_hashed: hashedABN,
    monthly_mc_spend: monthlySpend ?? '',
    mc_compound: compoundingPharmacy ?? '',
    ahpra_number_hashed: hashedAHPRA,
  });
};

export type PromoEventName = 'promo_view' | 'promo_click';

export type PromoData = {
  promo_campaign: string | null;
  promo_creative: string | null;
  promo_placement: string | null;
  promo_format: 'banner';
};

/**
 * Track a promo banner view or click in GTM.
 * @param eventName - Whether the banner was viewed or clicked.
 * @param promoData - The promo_* values set on the promo by CRM, plus the promo's format.
 */
export const trackPromoEvent = (eventName: PromoEventName, promoData: PromoData) => {
  if (isDataLayerExist()) {
    // GTM merges every push into one persistent data model, so a value omitted here would
    // silently carry over from the previous promo event - missing values are pushed as null.
    window.dataLayer.push({
      event: eventName,
      ...promoData,
    });
  }
};
